// import React from 'react';
// import { navItems } from '.';
// import mira from 'assets/mira.svg';
// import { Link } from 'react-router-dom';
// import { useSelector } from 'react-redux';
// import { userRoleData } from 'redux/slices/userRole';

// /**
//  * Renders a navigation bar component.
//  *
//  * @returns {JSX.Element} The rendered navigation bar component.
//  */
// const NavBar = () => {
//   const role = useSelector(userRoleData);

//   const filteredNavItems = navItems?.filter((item) =>
//     item?.role?.includes(role?.userRole)
//   );
//   return (
//         // <div className="flex flex-col w-[88px] h-screen bg-gradient-to-b from-[#011f71] to-[#0e60fb] transition-all duration-300 ease-in-out">
// <div className="flex flex-col w-[88px] h-screen" style={{ background: 'linear-gradient(180deg, #0F1434 0%, #0630AD 64.5%, #0E61FE 100%)' }}>

//     {/* // <div className="flex flex-col w-[88px] secondary-bg h-screen"> */}
//       {/* <div className="flex justify-center mt-4 mb-6">
//         <div className="flex justify-center primary-bg rounded-full w-[36px] h-[36px]  text-white">
//           <img className="w-[26px]" src={mira} alt="mira" />
//         </div>
//       </div> */}

//       <nav className="flex-1 overflow-y-auto scroll-hidden">
//         {filteredNavItems?.map((item, index) => (
//           <NavItem
//             key={index}
//             title={item.title}
//             icon={item.icon}
//             path={item.path}
//             subPaths={item.subPaths}
//           />
//         ))}
//       </nav>
//     </div>
//   );
// };

// const NavItem = ({ title, icon, path, subPaths }) => {
//   return (
//     <Link
//       to={path}
//       className={`flex flex-col items-center p-4  ${
//         location.pathname.endsWith(path) ||
//         subPaths?.some((path) => location.pathname.startsWith(path))
//           ? 'bg-[#073A86] transition duration-300 '
//           : 'hover:bg-[#073A86]'
//       }   `}
//     >
//       {icon && <div className="mb-2">{icon}</div>}
//       <span
//         className={`${
//           location.pathname.endsWith(path) ||
//           subPaths?.some((path) => location.pathname.startsWith(path))
//             ? 'font-medium-bold text-base_white text-center'
//             : 'font-medium text-base_white text-center'
//         }`}
//       >
//         {title}
//       </span>
//     </Link>
//   );
// };

// export default NavBar;

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { navItems } from '.';
import { canAccessSection } from '../../utils/accessControl';
import { Link, useLocation } from 'react-router-dom';
import { buildAppPath } from '@/utils/tenant';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux/slices/userRole';
import { selectUser } from 'redux/slices/authSlice';
import { performLogout } from '@/utils/logout';
import { LogOut, Menu } from 'lucide-react';
import AccountQuickPanel from '@/components/account/AccountQuickPanel';
import styles from './nav-bar.module.css';

const NavBar = () => {
  const role = useSelector(userRoleData);
  const user = useSelector(selectUser);
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountContainerRef = useRef(null);
  /* The modal surface is portalled to <body>, so it is not inside the anchor
     the click-away test checks. It needs its own ref or every click inside it
     would count as a click outside and close it. */
  const accountModalRef = useRef(null);

  const filteredNavItems = useMemo(() => navItems?.filter((item) => {
    if (item.section) {
      return canAccessSection(item.section, user?.originalRole || user?.role);
    }

    if (item.role) {
      return item.role.includes(role?.userRole);
    }

    return true;
  }), [user?.originalRole, user?.role, role?.userRole]);

  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'Mira User';
  const displayEmail = user?.email || '';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'MU';
  const avatarUrl =
    user?.profile_picture_url ||
    user?.profilePictureUrl ||
    user?.avatar_url ||
    user?.profile_picture ||
    '';
  const isConsumer =
    String(user?.originalRole || user?.role || '').toLowerCase() === 'consumer';

  const isActiveRoute = (path, subPaths = []) => {
    const currentPath = location.pathname;
    if (currentPath === path || currentPath.startsWith(path)) {
      return true;
    }
    return subPaths.some((subPath) => currentPath.startsWith(subPath));
  };

  // B2C pages that should carry the active application ID in the URL
  const b2cAppIdPaths = ['/profile', '/documents'];
  const buildNavLink = (path) => {
    const base = buildAppPath(path);
    if (b2cAppIdPaths.includes(path)) {
      const appId = sessionStorage.getItem('b2c_active_application_id');
      if (appId) return `${base}?applicationId=${encodeURIComponent(appId)}`;
    }
    return base;
  };

  const isLongRunningTaskActive = false;

  useEffect(() => {
    const handleClickAway = (event) => {
      const insideAnchor = accountContainerRef.current?.contains(event.target);
      const insideModal = accountModalRef.current?.contains(event.target);
      if (accountContainerRef.current && !insideAnchor && !insideModal) {
        setIsAccountOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  /* Dialog focus. Portalling the modal out of the rail is not enough on its
     own: focus stays on the profile button that opened it, which is inside
     the rail, and `.sidebar:focus-within` holds the rail at 16rem behind the
     scrim for as long as the dialog is up. Focus moves to the dialog on open
     and back to the button on close, which is what a dialog does anyway.
     Escape closes it — nothing did before. */
  useEffect(() => {
    if (!isAccountOpen || isConsumer) {
      return undefined;
    }
    const trigger = document.activeElement;
    accountModalRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (trigger instanceof HTMLElement) {
        trigger.focus();
      }
    };
  }, [isAccountOpen, isConsumer]);

  const handleSignOut = async () => {
    if (isLongRunningTaskActive) {
      setShowLogoutConfirm(true);
      return;
    }
    await performLogout({ redirect: true });
  };

  return (
    <>
      {/* Two surfaces, two places in the tree.

          The consumer popover is anchored to the profile row, so it has to
          live inside the rail, and the rail is raised above the backdrop so
          the popover can be clicked. That arrangement was being used for the
          B2B modal as well, and it is why the rail stayed bright while the
          rest of the page dimmed: the backdrop sits at z 70 and the rail was
          lifted to 85 on top of it, taking its background and icons with it.
          Focus inside the modal also matched `.sidebar:focus-within`, which
          widened the rail behind the dialog.

          The modal is centred and fixed, so it needs none of that. It is
          portalled to <body> with the backdrop, on the app's modal layers,
          and the rail is left where it is — under the scrim with everything
          else. */}
      {isAccountOpen && isConsumer ? (
        <button
          type="button"
          aria-label="Close account popup"
          className={styles.accountBackdrop}
          onClick={() => setIsAccountOpen(false)}
        />
      ) : null}

      {isAccountOpen && !isConsumer
        ? createPortal(
            <>
              <button
                type="button"
                aria-label="Close account panel"
                className={`${styles.accountBackdrop} ${styles.accountBackdropModal}`}
                onClick={() => setIsAccountOpen(false)}
              />
              <div
                ref={accountModalRef}
                className={styles.accountPanelModal}
                role="dialog"
                aria-modal="true"
                aria-label="Account"
                tabIndex={-1}
              >
                <AccountQuickPanel
                  user={user}
                  surface="modal"
                  onClose={() => setIsAccountOpen(false)}
                />
              </div>
            </>,
            document.body
          )
        : null}

      <aside
        className={`${styles.sidebar} ${isAccountOpen && isConsumer ? styles.sidebarAccountOpen : ''}`}
      >
        <div className={styles.sidebarInner}>
          {/* Both are rendered and CSS picks one — see .brandWordmark. The
              collapsed rail is 3.6rem here, too narrow for a wordmark, so it
              shows a menu glyph that reads as "there is more behind this" and
              the open rail shows Opus. The glyph is decorative: hovering the
              rail is what opens it, so there is nothing here to click and
              nothing for a screen reader to announce. */}
          <div className={styles.logoRow}>
            <Menu className={styles.brandMark} aria-hidden="true" />
            <img
              src="/opus-wordmark.png"
              alt="Opus Capital Markets Consultants"
              className={styles.brandWordmark}
            />
          </div>

          <nav className={styles.navList}>
            {filteredNavItems?.map((item) => {
              const active = isActiveRoute(item.path, item.subPaths || []);
              return (
                <Link
                  key={item.title}
                  to={buildNavLink(item.path)}
                  className={`${styles.actionButton} ${active ? styles.actionButtonActive : styles.actionButtonInactive}`}
                >
                  <div className={styles.navIconWrapper}>
                    {item.icon && (
                      <span
                        className={`${styles.navIcon} ${active ? styles.navIconActive : styles.navIconInactive}`}
                      >
                        {item.icon}
                      </span>
                    )}
                  </div>
                  <span className={`${styles.navLabel} ${active ? styles.navLabelActive : ''}`}>
                    {item.title}
                  </span>
                  <div className={styles.tooltip}>
                    {item.title}
                    <div className={styles.tooltipArrow} />
                  </div>
                </Link>
              );
            })}
          </nav>

          <div className={styles.footer}>
            <div ref={accountContainerRef} className={styles.accountPanelAnchor}>
              <button
                type="button"
                className={`${styles.profileRow} ${styles.profileRowButton}`}
                onClick={() => setIsAccountOpen((prev) => !prev)}
                aria-label="Open account panel"
                aria-expanded={isAccountOpen}
              >
                <div className={styles.profileAvatar}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className={styles.profileAvatarImage}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <div className={styles.profileText}>
                  <p className={styles.profileName}>{displayName}</p>
                  {displayEmail ? <p className={styles.profileEmail}>{displayEmail}</p> : null}
                </div>
              </button>

              {isAccountOpen && isConsumer ? (
                <div className={styles.accountPanelPopover}>
                  <AccountQuickPanel
                    user={user}
                    surface="popover"
                    onClose={() => setIsAccountOpen(false)}
                  />
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className={styles.signOutButton}
              onClick={handleSignOut}
              aria-label="Sign out"
            >
              <LogOut className={styles.lucideIcon} />
              <span className={styles.navLabel}>Sign out</span>
              <div className={styles.tooltip}>
                Sign out
                <div className={styles.tooltipArrow} />
              </div>
            </button>
          </div>
        </div>
      </aside>

      {showLogoutConfirm && (
        <div className={styles.logoutModalBackdrop}>
          <div className={styles.logoutModal}>
            <h3>Uploads in progress</h3>
            <p>
              A long-running upload is active. Signing out now may interrupt the process.
            </p>
            <div className={styles.logoutModalActions}>
              <button type="button" onClick={() => setShowLogoutConfirm(false)} className={styles.modalSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => performLogout({ redirect: true })}
                className={styles.modalPrimary}
              >
                Sign out anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
